import apiUrl from '../../../Shared/ApiUrl'
import React , { useState , useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {  Typography, IconButton, Stack, TextField, InputAdornment, Box, Paper, Button } from '@mui/material';
import { AddShoppingCartOutlined, AirOutlined, Replay, Clear as ClearIcon, SearchOff as SearchOffIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

import { LuServerOff } from 'react-icons/lu'

import './shippingStyles.css';
import DownloadButton from '../../../Shared/DownloadButton';
import { MainContainer, SkeletonLoad, Message } from '../../../Shared'
import TrackingItem from '../Tracking/trackingItem';

const getFormattedAddress = (row, type) => {
    if (!row) return '';
    const prefix = type === 'origin' ? 'origen' : 'destino';
    const persona = row[`${prefix}Persona`] || '';
    const municipio = row[`${prefix}Municipio`] || '';
    const estado = row[`${prefix}Estado`] || '';
    const cp = row[`${prefix}CP`] || '';
    
    return [persona, municipio, estado, cp].filter(Boolean).join(', ');
};

export default function WaybillsTable(props){
    /* Estados */
    const [ loading, setLoading ] = useState(true)
    const [ messageTitle, setMessageTitle ] = useState('')
    const [ messageContent, setMessageContent ] = useState('')
    const [ waybills , setWaybills ] = useState(null)
    const navigate = useNavigate();
    const userPermissions = JSON.parse(sessionStorage.getItem('permissions'))
    const [tableSearch, setTableSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [openTracking, setOpenTracking] = useState(false)
    const [trackingData, setTrackingData] = useState({})

    const envConfig = {
        url: apiUrl()
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(tableSearch)
        }, 500)

        return () => clearTimeout(timer)
    }, [tableSearch])

    const getWaybills = async () => {
        try {
            setLoading(true)

            const request = await fetch(`${envConfig.url}/api/v2/waybills`, {
                method : 'GET',
                headers : {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                    'Cache-Control': 'no-cache'
                }
            })
            
            if(request?.ok) {
                const response = await request.json()
                setWaybills( response?.data )
                if(response?.data.length === 0){
                    setMessageTitle('Sin guías')
                    setMessageContent('Para poder visualizar las guías, realiza una cotización en el modulo de Mi Carrito.')
                }
                return
            }

            throw new Error('Hubo un problema con el servidor-Ocurrio un problema al momento de traer las guías, intentelo más tarde.')
        } catch (error) {
            const requestErrorMessage = error.message.split('-')
            setMessageTitle(requestErrorMessage[0])
            setMessageContent(requestErrorMessage[1])
            setWaybills(null)
        } finally {
            setLoading(false)
        }
    }

    useEffect( () => {
        getWaybills();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    } , [] );

    const handleTrackingClick = useCallback(async (waybill) => {
        setOpenTracking(true)
        setTrackingData({})
        try {
            const response = await fetch(
                `${envConfig.url}/api/v2/tracking?waybills=${JSON.stringify([waybill])}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                        "Cache-Control": "no-cache"
                    }
                });
            const data = await response.json();
            if (data.data && data.data.length > 0) {
                setTrackingData(data.data[0]);
            }
        } catch (error) {
            console.error(error);
        }
    }, [envConfig.url])

    const columns = useMemo(() => [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
                    <DownloadButton type="waybill" identifier={params.row.numero} />
                    {userPermissions['role'] !== 'SubCliente' && params.row.ticketPagoOrigen && (
                        <DownloadButton type="ticket" identifier={params.row.ticketPagoOrigen} />
                    )}
                    <Button variant="outlined" size="small" onClick={() => handleTrackingClick(params.row.numero)}>Rastreo</Button>
                </Stack>
            )
        },
        { 
            field: 'created_at', 
            headerName: 'Fecha', 
            width: 150,
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return date.toLocaleString('es-MX');
            }
        },
        { field: 'numero', headerName: 'Número de Guía', width: 200, resizable: true },
        { 
            field: 'origen', 
            headerName: 'Origen', 
            width: 300,
            valueGetter: (value, row) => getFormattedAddress(row || value?.row, 'origin'),
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', lineHeight: '1.2', padding: '5px 0' }}>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.origenPersona}</span></Typography>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.origenCalle} {params.row.origenNumExt} {params.row.origenNumInt}</span></Typography>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.origenTelefono1}</span></Typography>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.origenMunicipio}, {params.row.origenEstado}, {params.row.origenCP}</span></Typography>
                </div>
            )
        },
        { 
            field: 'destino', 
            headerName: 'Destino', 
            width: 300,
            valueGetter: (value, row) => getFormattedAddress(row || value?.row, 'destination'),
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', lineHeight: '1.2', padding: '5px 0' }}>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.destinoPersona}</span></Typography>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.destinoCalle} {params.row.destinoNumExt} {params.row.destinoNumInt}</span></Typography>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.destinoTelefono1}</span></Typography>
                    <Typography variant="body2" fontWeight="bold"><span style={{fontWeight: 'normal', color: '#ff4500'}}>{params.row.destinoMunicipio}, {params.row.destinoEstado}, {params.row.destinoCP}</span></Typography>
                </div>
            )
        },
        { field: 'name', headerName: 'Servicio', width: 150 },
        { field: 'kg', headerName: 'Kilos', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'largo', headerName: 'Largo', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'alto', headerName: 'Alto', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'ancho', headerName: 'Ancho', width: 50, align: 'center', headerAlign: 'center' }
    ], [userPermissions, handleTrackingClick]);

    const filteredWaybills = useMemo(() => {
        if (!waybills) return [];
        if (!debouncedSearch) return waybills;

        const lowerSearch = debouncedSearch.toLowerCase();
        return waybills.filter(row => {
            const origin = getFormattedAddress(row, 'origin').toLowerCase();
            const destination = getFormattedAddress(row, 'destination').toLowerCase();
            const waybillNum = (row.numero || '').toLowerCase();
            return origin.includes(lowerSearch) || destination.includes(lowerSearch) || waybillNum.includes(lowerSearch);
        });
    }, [waybills, debouncedSearch]);

    return(
        <MainContainer>
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
            >
                <Typography
                    variant='h1'
                    sx={{ paddingY: 2 }}
                >
                    Tus guías
                </Typography>

                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-evenly'
                >
                    {/* <IconButton
                        color='success'
                        title='Crear guía masiva'
                        onClick={() => navigate('/massiveWaybills')}
                    >
                        <LibraryAddOutlined />
                    </IconButton> */}

                    <IconButton
                        color='primary'
                        title='Crear una cotización'
                        onClick={() => navigate('/MyShoppingCart')}
                    >
                        <AddShoppingCartOutlined />
                    </IconButton>
                </Stack>
            </Stack>

            {loading && (
                <SkeletonLoad 
                    types={['rectangular']}
                    numberOfItems={5}
                    randomHeight
                    heightRange={[50, 120]}
                />
            )}

            {!loading && (
                (waybills === null || waybills?.length === 0) ? (
                    <Message 
                        title={messageTitle}
                        message={messageContent}
                        icon={waybills === null ? <LuServerOff fontSize={50} /> : <AirOutlined sx={{ fontSize: 50 }} />}
                        callback={waybills === null ? getWaybills : () => navigate('/MyShoppingCart')}
                        buttonMessage={
                            <Stack direction='row' alignItems='center' justifyContent='center' gap={2} color='#ffffff' fontWeight='600' border='2px solid #ff4500' borderRadius={3} bgcolor='#ff4500' paddingY={0.3} paddingX={1} sx={{ '&:hover': { color: '#dddddd', border: '2px solid #d53a00', bgcolor: '#d53a00' } }}>
                                {waybills === null ? <Replay /> : <AddShoppingCartOutlined />}
                                <Typography fontSize={15}>{waybills === null ? 'Volver intentar' : 'Crear una cotización'}</Typography>
                            </Stack>
                        }
                    />
                ) : (
                    <Paper sx={{ width: '100%', height: '100%', minHeight: '30em', p: 2 }}>
                        <TextField 
                            fullWidth 
                            label="Buscar por Guía, Origen o Destino" 
                            variant="outlined" 
                            size="small"
                            sx={{ mb: 2 }}
                            value={tableSearch}
                            onChange={(e) => setTableSearch(e.target.value)}
                            InputProps={{
                                endAdornment: tableSearch && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setTableSearch('')} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {filteredWaybills.length > 0 ? (
                            <DataGrid
                                rows={filteredWaybills}
                                columns={columns}
                                pageSize={25}
                                rowsPerPageOptions={[25, 50, 100]}
                                getRowId={(row) => row.numero}
                                getRowHeight={() => 'auto'}
                                disableColumnResize={false}
                                autoHeight
                            />
                        ) : (
                            <Stack paddingX={'20%'} justifyContent='center' alignContent='center' sx={{ height: '100%', minHeight: '200px', textAlign: 'center' }}>
                                <Box><SearchOffIcon sx={{ fontSize: 75 }} /></Box>
                                <Typography variant='h5'>Sin resultados</Typography>
                                <Typography variant='body1'>No encontramos guías que cumplan los parámetros de búsqueda</Typography>
                            </Stack>
                        )}
                    </Paper>
                )
            )}
            <TrackingItem
                open={openTracking}
                onClose={() => setOpenTracking(false)}
                items={trackingData}
                title={"Rastreo"}
            />
        </MainContainer>
    );
}

/*

<>
            <div className = "container container--waybillsTable ">
                <div className = "containerGrid">
                    <Grid container spacing = { 2 }>
                        <Grid item xs = { 8 }
                        >
                            <Typography
                                variant = "body2"
                                sx = {{
                                    color : "#ff4500",
                                    fontWeight : "bold",
                                    fontSize : "23px",
                                    marginLeft : "270px"
                                }}
                            >
                                Tus Guias
                            </Typography>
                        </Grid>

                        <Grid
                            item
                            xs = { 4 }
                        >
                            <div align = "right">
                                {/* <ButtonLink 
                                    url = "/originOrDestination"
                                    clases = ""
                                    texto = "+Agregar" 
                                /> */

                                /*<IconButton 
                                    onClick = { () => goTo( "/massiveWaybills" ) }
                                    title = "Crear guia masiva"
                                    color = "success"
                                >
                                    <LibraryAdd/>
                                </IconButton>

                                <IconButton 
                                    onClick = { () => goTo( "/originOrDestination" ) }
                                    title = "Crear guia"
                                    color = "primary"
                                >
                                    <NoteAdd/>
                                </IconButton>
                            </div>
                        </Grid>

                        <Grid item xs = { 12 }>*/

                            /* Tabla de guias }
                            <TableContainer sx = {{ maxHeight: 440 }} component = {Paper}>
                                <Table stickyHeader aria-label="sticky table">

                                    <TableHead>
                                        <TableRow>
                                            { tableCellsHead }
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        { waybillsTableRows }
                                    </TableBody>
                                </Table>
                            </TableContainer>

                        </Grid>

                    </Grid>
                </div>
            </div>

            <ConfirmationDialog 
                open = { open }
                handleClose = { handleClose }
                waybillNumber = { waybillNumber }
            />
        </>
*/

/*return(
    <Container maxWidth="lg">
        <Paper elevation={3} sx={ { marginY: 5, paddingX: 5, paddingY: 5} }>

            
            <Container sx={ {marginBottom: 2, textAlign: "left"} }>
                <Grid container spacing={2}>

                    
                    <Grid item xs={12} md={9}>
                        <Typography variant='h5'>
                            Tus guias
                        </Typography>
                    </Grid>

                    
                    <Grid item xs={12} md={3}>
                        <div 
                            style = {{
                                display : "flex",
                                justifyContent : "right",
                                gap : 8
                            }}
                        >
                            <IconButton 
                                onClick = { () => goTo( "/massiveWaybills" ) }
                                title = "Crear guia masiva"
                                sx = {{
                                    backgroundColor : "#079000",
                                    color : "#ffffff",
                                    width : "35px",
                                    height : "35px",
                                    borderRadius : 2,
                                    border : "2px solid #079000",
                                    "&:hover" : {
                                        backgroundColor : "#056a00",
                                        color : "#dcdcdc",
                                        border : "2px solid #056a00"
                                    }
                                }}
                            >
                                <LibraryAdd />
                            </IconButton>

                            <IconButton 
                                onClick = { () => goTo( "/quotes" ) }
                                title = "Crear una cotización"
                                sx = {{
                                    backgroundColor : "#ff4500",
                                    color : "#ffffff",
                                    width : "35px",
                                    height : "35px",
                                    borderRadius : 2,
                                    border : "2px solid #ff4500",
                                    "&:hover" : {
                                        backgroundColor : "#e93f01",
                                        color : "#dcdcdc",
                                        border : "2px solid #e93f01"
                                    }
                                }}
                            >
                                <RequestQuote />
                            </IconButton>
                        </div>
                    </Grid>
                </Grid>
            </Container>

            
            <TableContainer>
                <Table stickyHeader aria-label="Tabla con encabezados estáticos">

                    <TableHead>
                        <TableRow>
                            { tableCellsHead }
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        { waybillsTableRows }
                    </TableBody>
                </Table>
            </TableContainer>
            
        </Paper>

        <ConfirmationDialog 
            open = { open }
            handleClose = { handleClose }
            waybillNumber = { waybillNumber }
        />

    </Container>
);*/